import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowDown, ArrowUp, ChevronRight, FolderOpen, MessageSquare, MessageSquarePlus, Pencil, Settings2, Trash2, Undo2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AttachButton, DropZone, PendingFiles } from "@/components/chat/Attachments";
import Composer, { ModelSelect, useDefaultModel } from "@/components/chat/Composer";
import MessageView from "@/components/chat/MessageView";
import { chatRenderers } from "@/components/chat/renderers";
import { ChatSpace } from "@/components/chat/ToolCall";
import SpaceSettings from "@/components/space/SpaceSettings";
import ViewCard from "@/components/space/Views";
import { ErrorText, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { usePendingFiles } from "@/lib/files";
import { useLive, withLive } from "@/lib/live";
import { spaceApi, useSpace, type View } from "@/lib/spaces";
import { usePageTitle } from "@/lib/tabStatus";
import { pathTo, buildTree } from "@/lib/tree";
import type { Chat, SendResult } from "@/lib/types";
import { primeSendResult, useChat } from "./ChatPage";

/** Views that read best across the whole page unless they say otherwise. */
const WIDE = new Set(["records", "table", "checklist", "calendar", "chart"]);

function wide(v: View) {
  return v.width ? v.width === "full" : WIDE.has(v.type);
}

/** The answer to a quick-box request, shown on the home page. */
function QuickReply({ chatId, onClose }: { chatId: string; onClose: () => void }) {
  const chat = useChat(chatId);
  const live = useLive((s) => s.live);
  const navigate = useNavigate();
  const qc = useQueryClient();
  useEffect(() => {
    chat.data?.messages.forEach((m) => useLive.getState().seed(m));
  }, [chat.data]);
  const path = useMemo(() => {
    const msgs = (chat.data?.messages ?? []).map((m) => withLive(m, live[m.id]));
    return pathTo(buildTree(msgs), chat.data?.chat.current_leaf_id ?? null);
  }, [chat.data, live]);
  const reply = path.filter((m) => m.role === "assistant").at(-1);

  async function open() {
    const settings = { ...(chat.data?.chat.settings ?? {}) };
    delete settings.quick;
    await api.patch(`/api/chats/${chatId}`, { settings });
    qc.invalidateQueries({ queryKey: ["chats", "list"] });
    navigate(`/c/${chatId}`);
  }

  if (!reply) return null;
  return (
    <div className="relative mt-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 pt-2 pb-3 dark:border-zinc-800 dark:bg-zinc-900/60">
      <button type="button" onClick={onClose} className="absolute top-2 right-2 rounded p-1 text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-800" aria-label="Close the answer">
        <X size={14} />
      </button>
      <MessageView message={reply} renderers={chatRenderers} busy={reply.status === "streaming"} />
      {reply.status !== "streaming" && (
        <button type="button" onClick={open} className="mt-1 flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400">
          <MessageSquare size={12} /> Continue as chat
        </button>
      )}
    </div>
  );
}

interface Toast {
  message: string;
  batch: string | null;
}

export default function SpaceHome() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const space = useSpace(id);
  usePageTitle(space.data?.name);
  const chats = useQuery({ queryKey: ["chats", "space", id], queryFn: () => api.get<Chat[]>(`/api/chats?folder_id=${id}&limit=5`), enabled: !!id });
  const defaultModel = useDefaultModel(space.data?.settings.model_id);
  const [model, setModel] = useState<string | null>(null);
  const modelId = model ?? defaultModel;
  const pending = usePendingFiles();
  const [quick, setQuick] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [editing, setEditing] = useState(false);
  const [settings, setSettings] = useState(false);
  const toastTimer = useRef<number | undefined>(undefined);
  const refresh = () => qc.invalidateQueries({ queryKey: ["space", id] });

  useEffect(() => {
    setQuick(null);
    setEditing(false);
  }, [id]);

  function changed(batch: string | null, message: string) {
    refresh();
    if (!message) return;
    window.clearTimeout(toastTimer.current);
    setToast({ message, batch });
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }

  async function undoToast() {
    if (!toast?.batch) return;
    try {
      await spaceApi.undo(id, toast.batch);
      setToast({ message: "Undone", batch: null });
      refresh();
    } catch (e) {
      setToast({ message: e instanceof Error ? e.message : "Could not undo", batch: null });
    }
  }

  async function ask(text: string, asChat = false) {
    setError(null);
    try {
      const r = await api.post<SendResult>("/api/chats", {
        model_id: modelId,
        content: text,
        parts: pending.parts,
        folder_id: id,
        settings: asChat ? {} : { quick: true },
      });
      pending.clear();
      primeSendResult(qc, r);
      if (asChat) navigate(`/c/${r.chat.id}`);
      else setQuick(r.chat.id);
    } catch (e) {
      setError(e);
      return false;
    }
  }

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put<{ batch: string }>(`/api/spaces/${id}/views/order`, { ids }),
    onSuccess: (r) => changed(r.batch, "Moved"),
    onError: setError,
  });
  const removeView = useMutation({
    mutationFn: (viewId: string) => api.delete<{ batch: string }>(`/api/spaces/${id}/views/${viewId}`),
    onSuccess: (r) => changed(r.batch, "Removed from the page"),
    onError: setError,
  });

  if (space.isLoading)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  if (space.error || !space.data)
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <ErrorText error={space.error} />
      </div>
    );

  const s = space.data;
  const views = s.views;
  const move = (i: number, d: number) => {
    const ids = views.map((v) => v.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    reorder.mutate(ids);
  };

  return (
    <ChatSpace.Provider value={id}>
      <DropZone onFiles={pending.add} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-zinc-200 px-4 pl-14 md:pl-4 dark:border-zinc-800">
          <nav className="flex min-w-0 flex-1 items-center gap-1 text-sm">
            <Link to="/spaces" className="text-zinc-500 hover:underline">
              Spaces
            </Link>
            <ChevronRight size={14} className="shrink-0 text-zinc-400" />
            <h1 className="truncate font-medium">{s.name}</h1>
            {s.role === "member" && <span className="shrink-0 text-xs text-zinc-500">(shared with you)</span>}
          </nav>
          {views.length > 0 && (
            <button
              type="button"
              onClick={() => setEditing(!editing)}
              className={clsx("flex items-center gap-1 rounded-lg px-2 py-1 text-xs", editing ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800")}
              aria-pressed={editing}
            >
              <Pencil size={14} /> <span className="hidden sm:inline">{editing ? "Done" : "Arrange"}</span>
            </button>
          )}
          <Link to={`/spaces/${id}/files`} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" title="Files">
            <FolderOpen size={14} /> <span className="hidden sm:inline">Files</span>
          </Link>
          <button type="button" onClick={() => setSettings(true)} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800" title="Settings, notes and history">
            <Settings2 size={14} /> <span className="hidden sm:inline">Settings</span>
          </button>
        </header>

        <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-4">
          <div>
            {s.description && views.length === 0 && <p className="mb-3 text-sm text-zinc-500">{s.description}</p>}
            <ErrorText error={error} />
            <Composer
              onSend={(text) => ask(text)}
              disabled={!modelId || pending.uploading}
              canSendEmpty={pending.parts.length > 0}
              onPasteFiles={pending.add}
              attachments={<PendingFiles files={pending.files} onRemove={pending.remove} />}
              placeholder={views.length ? `Add, ask or change something in ${s.name}` : `What is ${s.name} for?`}
              autoFocus={views.length === 0}
              toolbar={
                <>
                  <AttachButton onFiles={pending.add} />
                  <ModelSelect value={modelId} onChange={setModel} />
                </>
              }
            />
            {quick && <QuickReply key={quick} chatId={quick} onClose={() => setQuick(null)} />}
          </div>

          {views.length === 0 && (
            <div className="rounded-xl border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
              <p>This space has no home page yet.</p>
              <button
                type="button"
                onClick={() =>
                  ask(
                    s.collections.length
                      ? "Look at the data in this space and set up a useful home page for it. Describe the collections first if they are not described."
                      : "Help me set up this space: ask what it is for, then create the collections and a useful home page.",
                    true,
                  )
                }
                className="mt-2 font-medium text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Set it up in a chat
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {views.map((v, i) => (
              <div key={v.id} className={clsx("relative min-w-0", wide(v) && "md:col-span-2")}>
                {editing && (
                  <div className="absolute -top-2 right-2 z-10 flex gap-0.5 rounded-lg border border-zinc-200 bg-white p-0.5 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                    <button type="button" className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30 dark:hover:bg-zinc-800" disabled={i === 0 || reorder.isPending} onClick={() => move(i, -1)} aria-label="Move up">
                      <ArrowUp size={14} />
                    </button>
                    <button type="button" className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30 dark:hover:bg-zinc-800" disabled={i === views.length - 1 || reorder.isPending} onClick={() => move(i, 1)} aria-label="Move down">
                      <ArrowDown size={14} />
                    </button>
                    <button type="button" className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950" onClick={() => removeView.mutate(v.id)} aria-label="Remove from the page">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
                <ViewCard spaceId={id} view={v} onPrompt={(text) => ask(text)} onChanged={changed} />
              </div>
            ))}
          </div>

          <section className="pt-2">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-zinc-500">Chats in this space</h2>
              <Link to={`/?folder=${id}`} className="flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400">
                <MessageSquarePlus size={12} /> New chat
              </Link>
            </div>
            {chats.data?.length ? (
              <ul className="divide-y divide-zinc-200 rounded-xl border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
                {chats.data.map((c) => (
                  <li key={c.id}>
                    <Link to={`/c/${c.id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900">
                      <span className="truncate">{c.title}</span>
                      <span className="shrink-0 text-xs text-zinc-500">{new Date(c.updated_at).toLocaleDateString()}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-zinc-500">No chats yet. Requests in the box above stay out of the chat list unless you continue them.</p>
            )}
          </section>
        </div>

        {toast && (
          <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
            <div className="pointer-events-auto flex items-center gap-3 rounded-lg bg-zinc-900 px-4 py-2 text-sm text-white shadow-lg dark:bg-zinc-100 dark:text-zinc-900">
              {toast.message}
              {toast.batch && (
                <button type="button" onClick={undoToast} className="flex items-center gap-1 font-medium text-indigo-300 hover:underline dark:text-indigo-700">
                  <Undo2 size={14} /> Undo
                </button>
              )}
            </div>
          </div>
        )}
        {settings && <SpaceSettings space={s} onClose={() => setSettings(false)} />}
      </DropZone>
    </ChatSpace.Provider>
  );
}
